import { Injectable } from '@angular/core';
import { BrandService } from '../../services/brand-config.service';
import { PlatformService } from './platform.service';
import { TranslationService } from './translation.service';

/** The two stores the Markable app is published in. */
export type StoreOs = 'ios' | 'android';

/**
 * The public listings. Both verified live (iTunes lookup + a 200 on the Play
 * page) — the App Store id belongs to "Markable Life" (bundle com.markable.app)
 * and the Play package is com.markable.life, which differs because
 * com.markable.app was already taken on Play.
 */
export const APP_STORE_URL =
  'https://apps.apple.com/il/app/markable-life/id6779954243';
export const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.markable.life';

/** The one short link behind the QR (spec §3): it picks the store by device. */
export const GET_APP_PATH = '/get';

/**
 * Everything the "download the Markable app" surfaces need to decide what to
 * show (app-download spec, Danna 2026-09-17).
 *
 * Two placements share this: the card at the end of her report and the row in
 * the profile. Both are hidden inside the native app itself and on Peritale,
 * which has no app in the stores.
 */
@Injectable({ providedIn: 'root' })
export class AppDownloadService {
  constructor(
    private brand: BrandService,
    private platform: PlatformService,
    private translation: TranslationService,
  ) {}

  /**
   * Whether a download surface may render at all. Markable only (Peritale has
   * no app), and never inside the installed app — she is already there.
   */
  get available(): boolean {
    return this.brand.isMarkableHost && this.platform.isWeb;
  }

  /** The store matching the browser she is in, or null on a desktop. */
  get deviceOs(): StoreOs | null {
    if (typeof navigator === 'undefined') return null;
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
    // iPadOS 13+ reports as desktop Safari; the touch points give it away.
    if (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1) {
      return 'ios';
    }
    if (/Android/i.test(ua)) return 'android';
    return null;
  }

  /** Desktop = no store to send her to, so we show the code to scan instead. */
  get isDesktop(): boolean {
    return this.deviceOs === null;
  }

  storeUrl(os: StoreOs): string {
    return os === 'ios' ? APP_STORE_URL : GOOGLE_PLAY_URL;
  }

  /**
   * The official store artwork, in her language. Apple and Google both require
   * the badge be used as supplied, so these are the files their badge services
   * return — never recoloured or reshaped.
   */
  badgeSrc(os: StoreOs): string {
    const he = this.translation.getCurrentLanguage() === 'he';
    return os === 'ios'
      ? `assets/markable/store/app-store-${he ? 'he' : 'en'}.svg`
      : `assets/markable/store/google-play-${he ? 'he' : 'en'}.png`;
  }

  /** Absolute URL for the QR, so a code scanned on staging stays on staging. */
  get qrUrl(): string {
    return `${window.location.origin}${GET_APP_PATH}`;
  }
}
